import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { getTableSelectionColors } from '../../../theme/uiSemanticColors';
import {
  Box,
  Table,
  TableContainer,
  CircularProgress,
} from '@mui/material';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import type { EnhancedTableProps, RowData, EditingCell } from './types';
import EnhancedTableToolbar from './EnhancedTableToolbar';
import EnhancedTableHead from './EnhancedTableHead';
import EnhancedTableBody from './EnhancedTableBody';
import BulkActionBar from './BulkActionBar';
import EnhancedTablePagination from './EnhancedTablePagination';
import { enhancedTableSurfaceSx, stickyHeaderTableSx } from './tableDividerStyles';
import { scrollbarSx } from '../../../theme/scrollbar';
import { AppTableAddFab } from '../../design-system';

/**
 * EnhancedTable - Lightweight, feature-rich table component
 * Built with MUI Table (not DataGrid) for server-side operations
 */
function EnhancedTable<T extends object = RowData>(props: EnhancedTableProps<T>) {
  const muiTheme = useTheme();
  const tableSelection = useMemo(() => getTableSelectionColors(muiTheme), [muiTheme]);
  const {
    data,
    totalCount,
    loading = false,
    columns,
    getRowId = (row: T) => (row as Record<string, unknown> & { id: string | number }).id,
    page,
    pageSize,
    pageSizeOptions = [10, 25, 50, 100],
    hidePagination = false,
    hidePaginationOnSinglePage = true,
    onPageChange,
    onPageSizeChange,
    sortBy,
    sortOrder,
    onSort,
    searchable = false,
    searchTerm = '',
    searchPlaceholder = 'Search...',
    onSearchChange,
    searchDebounceMs = 300,
    filters = {},
    filterOptions = [],
    filterDefaultValues,
    onFilterChange,
    selectable = false,
    selectionMode = 'multiple',
    selectedIds = [],
    onSelectionChange,
    activeRowId,
    actions = [],
    bulkActions = [],
    onRowClick,
    onRowContextMenu,
    emptyStateTitle = 'No data found',
    emptyStateDescription = 'There are no items to display',
    emptyStateAction,
    stickyHeader = true,
    dense = false,
    rowHeight: rowHeightProp,
    maxHeight = 'calc(100vh - 280px)',
    fitContent = false,
    tableLayout = 'auto',
    scrollToBottomKey,
    onRefresh,
    refreshTitle = 'Refresh',
    onAdd,
    addLabel,
    toolbarActions = [],
    toolbarLeftContent,
    exportable = false,
    onExport,
    groupBy,
    getGroupLabel,
    groupDisplayMode = 'section-header',
    renderGroupHeader,
    groupColumnHeader,
  } = props;

  const rowHeight = rowHeightProp ?? (dense ? 40 : 56);
  const cellPadding = dense ? '6px 8px' : '16px 8px';

  // State for inline editing
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollToBottomKey === undefined) return;
    const container = tableContainerRef.current;
    if (!container) return;
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
  }, [scrollToBottomKey, data.length, page]);

  // Visible columns (exclude hidden ones)
  const visibleColumns = useMemo(
    () => columns.filter((col) => !col.hide),
    [columns]
  );

  const displayData = useMemo(() => {
    if (!groupBy) return data;
    return [...data].sort((a, b) => {
      const ga = groupBy(a);
      const gb = groupBy(b);
      const labelCmp = (getGroupLabel?.(ga) ?? ga).localeCompare(getGroupLabel?.(gb) ?? gb);
      if (labelCmp !== 0) return labelCmp;
      return 0;
    });
  }, [data, groupBy, getGroupLabel]);

  const useGroupColumn = Boolean(groupBy) && groupDisplayMode === 'column';
  const resolvedGroupColumnHeader = useGroupColumn ? (groupColumnHeader ?? 'Group') : undefined;

  // Check if any row is selected
  const hasSelection = selectedIds.length > 0;

  // Handle select all
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!onSelectionChange || selectionMode === 'single') return;
      if (checked) {
        const allIds = displayData.map((row) => getRowId(row));
        onSelectionChange(allIds);
      } else {
        onSelectionChange([]);
      }
    },
    [displayData, getRowId, onSelectionChange, selectionMode]
  );

  // Handle single row selection
  const handleSelectRow = useCallback(
    (rowId: string | number, checked: boolean) => {
      if (!onSelectionChange) return;
      if (selectionMode === 'single') {
        onSelectionChange(checked ? [rowId] : []);
        return;
      }
      if (checked) {
        onSelectionChange([...selectedIds, rowId]);
      } else {
        onSelectionChange(selectedIds.filter((id) => id !== rowId));
      }
    },
    [selectedIds, onSelectionChange, selectionMode]
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (field: string) => {
      if (!onSort) return;
      
      // Toggle sort order: ASC -> DESC -> none
      let newOrder: 'ASC' | 'DESC' = 'ASC';
      
      if (sortBy === field) {
        if (sortOrder === 'ASC') {
          newOrder = 'DESC';
        } else {
          // If already DESC, we could clear sort, but for now just toggle
          newOrder = 'ASC';
        }
      }
      
      onSort(field, newOrder);
    },
    [sortBy, sortOrder, onSort]
  );

  // Handle cell edit — also activate the row (editable cells stop row click propagation)
  const handleCellEdit = useCallback(
    (rowId: string | number, field: string, value: unknown) => {
      const row = displayData.find((r) => getRowId(r) === rowId);
      if (row && onRowClick) {
        onRowClick(row);
      }
      setEditingCell({ rowId, field, value });
    },
    [displayData, getRowId, onRowClick],
  );

  // Handle cell save
  const handleCellSave = useCallback(
    async (rowId: string | number, field: string, newValue: unknown) => {
      const column = columns.find((col) => col.field === field);
      const row = displayData.find((r) => getRowId(r) === rowId);
      
      if (!column || !row || !column.editOptions?.onSave) {
        setEditingCell(null);
        return;
      }

      try {
        await column.editOptions.onSave(row, field, newValue);
        setEditingCell(null);
      } catch (error) {
        console.error('Failed to save cell:', error);
        // Keep editing state on error so user can retry
      }
    },
    [columns, displayData, getRowId]
  );

  // Handle cell cancel
  const handleCellCancel = useCallback(() => {
    setEditingCell(null);
  }, []);

  const handleRowClick = useCallback(
    (row: T) => {
      if (selectable && selectionMode === 'single' && onSelectionChange) {
        onSelectionChange([getRowId(row)]);
      }
      onRowClick?.(row);
    },
    [selectable, selectionMode, onSelectionChange, getRowId, onRowClick],
  );

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'transparent',
        position: 'relative',
        ...(fitContent
          ? {}
          : {
              flex: 1,
              minHeight: 0,
              maxHeight,
            }),
      }}
    >
      {/* Toolbar */}
      <EnhancedTableToolbar
        searchable={searchable}
        searchTerm={searchTerm}
        searchPlaceholder={searchPlaceholder}
        onSearchChange={onSearchChange}
        searchDebounceMs={searchDebounceMs}
        filters={filters}
        filterOptions={filterOptions}
        filterDefaultValues={filterDefaultValues}
        onFilterChange={onFilterChange}
        onRefresh={onRefresh}
        refreshTitle={refreshTitle}
        toolbarActions={toolbarActions}
        toolbarLeftContent={toolbarLeftContent}
        exportable={exportable}
        onExport={onExport}
      />

      {/* Bulk Action Bar */}
      {hasSelection && bulkActions.length > 0 && (
        <BulkActionBar
          selectedCount={selectedIds.length}
          bulkActions={bulkActions}
          selectedIds={selectedIds}
          onClearSelection={() => onSelectionChange?.([])}
        />
      )}

      {/* Table */}
      <TableContainer
        component={Box}
        ref={tableContainerRef}
        sx={{
          ...(fitContent ? { flex: 'none', overflow: 'visible' } : { flex: 1, minHeight: 0, overflow: 'auto' }),
          position: 'relative',
          bgcolor: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
          ...scrollbarSx(muiTheme),
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: tableSelection.loadingOverlay,
              backdropFilter: 'blur(2px)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <Table
          stickyHeader={stickyHeader}
          sx={[
            enhancedTableSurfaceSx,
            stickyHeaderTableSx(stickyHeader),
            tableLayout === 'fixed' ? { tableLayout: 'fixed', width: '100%' } : {},
          ] as SxProps<Theme>}
        >
          <EnhancedTableHead
            columns={visibleColumns}
            selectable={selectable}
            selectionMode={selectionMode}
            allSelected={selectedIds.length === displayData.length && displayData.length > 0}
            someSelected={selectedIds.length > 0 && selectedIds.length < displayData.length}
            onSelectAll={handleSelectAll}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSortChange}
            hasActions={actions.length > 0}
            cellPadding={cellPadding}
            groupColumnHeader={resolvedGroupColumnHeader}
          />

          <EnhancedTableBody
            data={displayData}
            columns={visibleColumns}
            getRowId={getRowId}
            selectable={selectable}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onSelectRow={handleSelectRow}
            onRowClick={handleRowClick}
            onRowContextMenu={onRowContextMenu}
            activeRowId={activeRowId}
            rowHeight={rowHeight}
            cellPadding={cellPadding}
            actions={actions}
            editingCell={editingCell}
            onCellEdit={handleCellEdit}
            onCellSave={handleCellSave}
            onCellCancel={handleCellCancel}
            loading={loading}
            emptyStateTitle={emptyStateTitle}
            emptyStateDescription={emptyStateDescription}
            emptyStateAction={emptyStateAction}
            groupBy={groupBy}
            getGroupLabel={getGroupLabel}
            groupDisplayMode={groupDisplayMode}
            renderGroupHeader={renderGroupHeader}
          />
        </Table>
      </TableContainer>

      {!hidePagination && (
        <EnhancedTablePagination
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          hidePaginationOnSinglePage={hidePaginationOnSinglePage}
        />
      )}

      {onAdd ? (
        <AppTableAddFab
          onClick={onAdd}
          label={addLabel}
          bottomOffset={hidePagination ? 16 : 88}
        />
      ) : null}
    </Box>
  );
}

export default EnhancedTable;
